import React, { useState } from "react";
import { Modal, Button, Form, FloatingLabel } from "react-bootstrap";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import Swal from "sweetalert2";

const CustomerModalAdd = ({ show, handleClose, handleAddUser }) => {
  const [formData, setFormData] = useState({
    email: "",
    username: "",
    password: "",
    confirmPassword: "",
    role: "ADMIN",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [passwordError, setPasswordError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: null }));
    }

    // Check password match when typing confirm password
    if (name === "confirmPassword" || name === "password") {
      if (name === "confirmPassword" && formData.password !== value) {
        setPasswordError("Password dan Konfirmasi tidak sama!");
      } else if (name === "password" && formData.confirmPassword && formData.confirmPassword !== value) {
        setPasswordError("Password dan Konfirmasi tidak sama!");
      } else {
        setPasswordError("");
      }
    }
  };

  // Validate form
  const validateForm = () => {
    const errors = {};

    if (!formData.email) {
      errors.email = "Email harus diisi";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = "Email tidak valid";
    }

    if (!formData.username) {
      errors.username = "Username harus diisi";
    } else if (formData.username.length < 3) {
      errors.username = "Username minimal 3 karakter";
    }

    if (!formData.password) {
      errors.password = "Password harus diisi";
    } else if (formData.password.length < 6) {
      errors.password = "Password minimal 6 karakter";
    }

    if (!formData.confirmPassword) {
      errors.confirmPassword = "Konfirmasi password harus diisi";
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = "Password dan Konfirmasi tidak sama!";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Hapus confirmPassword sebelum dikirim
      const { confirmPassword, ...userData } = formData;

      const result = await handleAddUser(userData);
      if (result.success) {
        // Reset form
        setFormData({
          email: "",
          username: "",
          password: "",
          confirmPassword: "",
        });
        
        // Tampilkan pesan sukses
        Swal.fire({
          icon: "success",
          title: "Berhasil!",
          text: "User berhasil ditambahkan",
          timer: 2000,
          showConfirmButton: false,
        });
        
        // Tutup modal
        handleClose();
      }
    } catch (error) {
      console.error("Error adding user:", error);
      Swal.fire({
        icon: "error",
        title: "Gagal! username dan email sudah digunakan",
        text: error.message || "Terjadi kesalahan saat menambahkan user",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset form when modal closes
  const handleModalClose = () => {
    setFormData({
      email: "",
      username: "",
      password: "",
      confirmPassword: "",
    });
    setFormErrors({});
    setPasswordError("");
    setShowPassword(false);
    setShowConfirmPassword(false);
    handleClose();
  };

  return (
    <Modal show={show} onHide={handleModalClose} size="md" centered>
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title className="fw-bold text-danger">
          Tambah User Baru
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body className="pt-2">
          <div className="px-2">
            <h6 className="text-danger border-bottom pb-2 mb-3">
              Informasi Akun Dasar
            </h6>

            {/* Email Field */}
            <FloatingLabel controlId="email" label="Email *" className="mb-3">
              <Form.Control
                type="email"
                name="email"
                placeholder="Email"
                value={formData.email}
                onChange={handleChange}
                isInvalid={!!formErrors.email}
                autoComplete="off"
              />
              <Form.Control.Feedback type="invalid">
                {formErrors.email}
              </Form.Control.Feedback>
            </FloatingLabel>

            {/* Username Field */}
            <FloatingLabel controlId="username" label="Username *" className="mb-3">
              <Form.Control
                type="text"
                name="username"
                placeholder="Username"
                value={formData.username}
                onChange={handleChange}
                isInvalid={!!formErrors.username}
                autoComplete="off"
              />
              <Form.Control.Feedback type="invalid">
                {formErrors.username}
              </Form.Control.Feedback>
            </FloatingLabel>

            {/* Password Field with Eye Icon */}
            <div className="position-relative mb-3">
              <FloatingLabel controlId="password" label="Password *">
                <Form.Control
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder="Password"
                  value={formData.password}
                  onChange={handleChange}
                  isInvalid={!!formErrors.password}
                  autoComplete="new-password"
                />
                <Form.Control.Feedback type="invalid">
                  {formErrors.password}
                </Form.Control.Feedback>
              </FloatingLabel>
              <button
                type="button"
                className="btn btn-link position-absolute end-0 top-50 translate-middle-y text-decoration-none"
                style={{ zIndex: 10, marginRight: "10px", marginTop: "5px" }}
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
              </button>
            </div>

            {/* Confirm Password Field with Eye Icon */}
            <div className="position-relative mb-2">
              <FloatingLabel controlId="confirmPassword" label="Konfirmasi Password *">
                <Form.Control
                  type={showConfirmPassword ? "text" : "password"}
                  name="confirmPassword"
                  placeholder="Konfirmasi Password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  isInvalid={!!formErrors.confirmPassword || !!passwordError}
                  autoComplete="new-password"
                />
                <Form.Control.Feedback type="invalid">
                  {formErrors.confirmPassword || passwordError}
                </Form.Control.Feedback>
              </FloatingLabel>
              <button
                type="button"
                className="btn btn-link position-absolute end-0 top-50 translate-middle-y text-decoration-none"
                style={{ zIndex: 10, marginRight: "10px", marginTop: "5px" }}
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
              </button>
            </div>

            {/* Password Hint */}
            <div className="text-muted small mt-1 mb-3">
              <i className="fas fa-info-circle me-1"></i>
              Minimal 6 karakter
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer className="border-0 pt-0">
          <Button
            variant="secondary"
            onClick={handleModalClose}
            disabled={isSubmitting}
            className="px-4"
          >
            Batal
          </Button>
          <Button
            variant="danger"
            type="submit"
            disabled={isSubmitting}
            className="px-4"
          >
            {isSubmitting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2"></span>
                Menyimpan...
              </>
            ) : (
              "Buat Akun"
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default CustomerModalAdd;